import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { TypewriterText } from '../hooks/useTypewriter';
import Verdict, { formatDay } from '../components/Verdict';

const VERDICTS = ['alive', 'dead', 'open', 'retracted', 'reference'];

/** The lab notebook: methods, results and conclusions, newest work first. */
export default function Developments() {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(null);
  const [params, setParams] = useSearchParams();
  const thread = params.get('thread') || '';
  const verdict = params.get('verdict') || '';

  useEffect(() => {
    document.title = 'developments · logOS';
    fetch('/api/developments')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('failed to load developments'))))
      .then((d) => setEntries(d.developments || []))
      .catch((e) => setError(e.message));
  }, []);

  const threads = useMemo(
    () => [...new Set((entries || []).map((e) => e.thread).filter(Boolean))].sort(),
    [entries],
  );
  const shown = (entries || []).filter(
    (e) => (!thread || e.thread === thread) && (!verdict || e.verdict === verdict),
  );
  const pick = (key, value) => {
    const next = new URLSearchParams(params);
    if (!value || next.get(key) === value) next.delete(key); else next.set(key, value);
    setParams(next, { replace: true });
  };
  const chip = (active) =>
    `px-2.5 py-1 rounded-full border text-xs lowercase transition-colors ${
      active ? 'border-white/50 text-white bg-white/5' : 'border-[#262d27] text-[#777777] hover:text-white hover:border-[#3a413a]'
    }`;

  return (
    <div className="min-h-screen bg-black py-12 lg:py-20 px-6 lg:px-8 page-wrapper" data-testid="developments-page">
      <div className="max-w-3xl mx-auto space-y-10">
        <section>
          <p className="text-[#555555] lowercase text-sm mb-2">lab notebook</p>
          <h1 className="text-4xl lg:text-5xl font-bold text-white lowercase mb-4">
            <TypewriterText text="developments" speed={65} startDelay={150} />
          </h1>
          <p className="text-[#aaaaaa] lowercase leading-relaxed">
            the research behind soma. what worked, what didn't, what's still open. entries are versioned:
            a correction is a new version, and the old one stays citable.
          </p>
        </section>

        {entries && entries.length > 0 && (
          <section className="space-y-3" data-testid="developments-filters">
            {threads.length > 0 && (
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-[10px] uppercase tracking-wider text-[#555555] w-14">thread</span>
                {threads.map((t) => (
                  <button key={t} onClick={() => pick('thread', t)} className={chip(thread === t)}>{t}</button>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-[10px] uppercase tracking-wider text-[#555555] w-14">verdict</span>
              {VERDICTS.filter((v) => entries.some((e) => e.verdict === v)).map((v) => (
                <button key={v} onClick={() => pick('verdict', v)} className={chip(verdict === v)}>{v}</button>
              ))}
            </div>
          </section>
        )}

        {error && <div className="text-[#ff8888] lowercase text-sm">{error}</div>}
        {!entries && !error && <div className="text-[#666666] lowercase text-sm animate-pulse">loading…</div>}
        {entries && entries.length === 0 && (
          <div className="py-12 text-center space-y-2" data-testid="developments-empty">
            <p className="text-white lowercase text-lg">nothing published yet.</p>
            <p className="text-[#555555] lowercase text-sm">the first entries are being written up.</p>
          </div>
        )}

        <ol className="divide-y divide-[#1a201b] border-y border-[#1a201b]" data-testid="developments-list">
          {shown.map((e) => (
            <li key={e.slug}>
              <Link
                to={`/developments/${e.slug}`}
                className="group block py-6"
                data-testid={`development-${e.slug}`}
              >
                <div className="flex items-baseline justify-between gap-3 flex-wrap text-xs font-mono lowercase">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <Verdict verdict={e.verdict} />
                    <span className="text-[#666666]">{e.kind}</span>
                    {e.thread && <span className="text-[#555555]">· {e.thread}</span>}
                  </div>
                  <span className="text-[#555555]">
                    {formatDay(e.occurred_on || e.published_at)}{e.version > 1 ? ` · v${e.version}` : ''}
                  </span>
                </div>
                <h2 className="text-white text-lg lg:text-xl mt-3 lowercase group-hover:translate-x-0.5 transition-transform duration-300">
                  {e.title}
                </h2>
                {e.summary && <p className="text-[#999999] text-sm leading-relaxed mt-2 lowercase">{e.summary}</p>}
                {e.evidence && <p className="text-[#555555] text-xs font-mono mt-3 lowercase">evidence · {e.evidence}</p>}
              </Link>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
